import { inject, injectable } from 'inversify-props';
import { IHttpClient, ILogger, ISettings } from '../common';
import { PagedResult, Category, DeckCategory, CategorySearchRequest, SubjectAggregate, CategoryImageRequest} from '../../models';
import { IBaseService } from '../BaseService';


export interface ICategoryService extends IBaseService<Category, number> {
	getMultiple(ids: number[]): Promise<Category[]>;
	list(): Promise<Category[]>;
	getByName(name: string): Promise<Category>;
	page(pageIndex: number, pageSize: number): Promise<PagedResult<Category>>;
	autocomplete(crtieria: string): Promise<Category[]>;
	search(criteria: CategorySearchRequest): Promise<PagedResult<Category>>;
	pageByDeck(deckId: string, pageIndex: number, pageSize: number): Promise<PagedResult<DeckCategory>>;
	listByDeck(deckId: string): Promise<DeckCategory[]>;
	listByCompany(companyId: string, pageIndex: number, pageSize: number): Promise<PagedResult<SubjectAggregate<Category>>>;
	uploadImage(request: CategoryImageRequest): Promise<any>;
}


@injectable()
export class CategoryService implements ICategoryService {

	constructor(
		@inject('IHttpClient') private httpClient: IHttpClient,
		@inject('ILogger') private logger: ILogger,
		@inject('ISettings') private settings: ISettings) { }

	public get(id: number): Promise<Category> {
		const url = `${this.settings.baseUrl}${this.settings.categoryUrl}/${id}`;
		return this.httpClient.get<Category>(url);
	}

	public getByName(name: string): Promise<Category> {
		const url = `${this.settings.baseUrl}${this.settings.categoryUrl}/by-name/${name}`;
		return this.httpClient.get<Category>(url);
	}

	public getMultiple(ids: number[]): Promise<Category[]> {
		const url = `${this.settings.baseUrl}${this.settings.categoryUrl}/multiple/${ids.join(',')}`;
		return this.httpClient.get<Category[]>(url);
	}

	public autocomplete(criteria: string): Promise<Category[]> {
		const url = `${this.settings.baseUrl}${this.settings.categoryUrl}/autocomplete/${criteria}`;
		return this.httpClient.get<Category[]>(url);
	}

	public search(criteria: CategorySearchRequest): Promise<PagedResult<Category>> {
		const url = `${this.settings.baseUrl}${this.settings.categoryUrl}/search`;
		return this.httpClient.post<PagedResult<Category>>(url, criteria);
	}

	public page(pageIndex: number, pageSize: number): Promise<PagedResult<Category>> {
		const url = `${this.settings.baseUrl}${this.settings.categoryUrl}/${pageIndex}/${pageSize}`;
		return this.httpClient.get<PagedResult<Category>>(url);
	}

	public listByCompany(companyId: string, pageIndex: number, pageSize: number): Promise<PagedResult<SubjectAggregate<Category>>> {
		const url = `${this.settings.baseUrl}${this.settings.categoryUrl}/by-company/${companyId}/${pageIndex}/${pageSize}`;
		return this.httpClient.get<PagedResult<SubjectAggregate<Category>>>(url);
	}

	public list(): Promise<Category[]> {
		const url = `${this.settings.baseUrl}${this.settings.categoryUrl}`;
		return this.httpClient.get<Category[]>(url);
	}

	public save(model: Category): Promise<Category> {
		let url = `${this.settings.baseUrl}${this.settings.categoryUrl}`;
		if (model.id !== null && model.id !== undefined && model.id) {
			url += `/${model.id}`;
			return this.httpClient.put(url, model);
		}
		return this.httpClient.post(url, model);
	}

	public delete(id: number): Promise<any> {
		const url = `${this.settings.baseUrl}${this.settings.categoryUrl}/${id}`;
		return this.httpClient.delete(url);
	}

	public uploadImage(request: CategoryImageRequest): Promise<any> {
		const url = `${this.settings.baseUrl}${this.settings.categoryUrl}/${request.id}/image`;
		return this.httpClient.put(url, request);
	}

	public listByDeck(deckId: string): Promise<DeckCategory[]> {
		const url = `${this.settings.baseUrl}${this.settings.deckBaseUrl}/${deckId}/categories`;
		return this.httpClient.get<DeckCategory[]>(url);
	}

	public pageByDeck(deckId: string, pageIndex: number, pageSize: number): Promise<PagedResult<DeckCategory>> {
		const url = `${this.settings.baseUrl}${this.settings.deckBaseUrl}/${deckId}/categories/${pageIndex}/${pageSize}`;
		return this.httpClient.get<PagedResult<DeckCategory>>(url);
	}


}
